import React, { useMemo, useState } from "react";
import { Box, Chip, InputBase, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AgentAvatar from "../pages/AgentAvatar";

export default function PeoplePicker({
  label,
  placeholder = "Search people...",
  items = [],
  selectedIds = [],
  onToggle,
  getId,
  getName,
  getDetail,
  getAvatarLabel,
  disabled = false,
  loading = false,
  emptyStateLabel = "None available",
}) {
  const [query, setQuery] = useState("");
  const safeItems = Array.isArray(items) ? items : [];
  const safeSelectedIds = Array.isArray(selectedIds) ? selectedIds : [];

  const filtered = useMemo(() => {
    if (!query.trim()) return safeItems;
    const q = query.toLowerCase();
    return safeItems.filter((item) => {
      const name = String(getName?.(item) || "").toLowerCase();
      const detail = String(getDetail?.(item) || "").toLowerCase();
      return name.includes(q) || detail.includes(q);
    });
  }, [safeItems, query, getName, getDetail]);

  const selectedItems = useMemo(
    () =>
      safeItems.filter((item) => {
        const id = getId?.(item);
        return id != null && safeSelectedIds.includes(id);
      }),
    [safeItems, safeSelectedIds, getId]
  );

  const handleToggle = (id) => {
    if (disabled || typeof onToggle !== "function") return;
    onToggle(id);
  };

  return (
    <Box sx={{ opacity: disabled ? 0.6 : 1 }}>
      {label ? (
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.75 }}
        >
          {label}
        </Typography>
      ) : null}
      {selectedItems.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
          {selectedItems.map((item) => {
            const id = getId(item);
            const name = getName(item) || String(id || "").slice(0, 8);
            return (
              <Chip
                key={id}
                label={name}
                size="small"
                onDelete={disabled ? undefined : () => handleToggle(id)}
                deleteIcon={<CloseIcon sx={{ fontSize: "12px !important" }} />}
                avatar={
                  <AgentAvatar name={name} size={20} borderRadius="4px" />
                }
                sx={{
                  height: 26,
                  fontSize: 12,
                  fontWeight: 500,
                  backgroundColor: "action.hover",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "6px",
                  color: "text.primary",
                  "& .MuiChip-deleteIcon": {
                    color: "text.disabled",
                    "&:hover": { color: "text.secondary" },
                  },
                  "& .MuiChip-avatar": { ml: "4px" },
                }}
              />
            );
          })}
        </Box>
      )}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: disabled ? "action.hover" : "background.paper",
        }}
      >
        <InputBase
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          sx={{
            width: "100%",
            px: 1.25,
            py: 0.75,
            fontSize: 13,
            color: "text.primary",
            borderBottom: "1px solid",
            borderColor: "divider",
            "& input::placeholder": { color: "text.disabled", opacity: 1 },
          }}
        />
        <Box sx={{ maxHeight: 280, overflowY: "auto", py: 0.5 }}>
          {loading ? (
            <Typography sx={{ fontSize: 12, color: "text.disabled", px: 1.25, py: 1 }}>
              Loading...
            </Typography>
          ) : filtered.length === 0 ? (
            <Typography sx={{ fontSize: 12, color: "text.disabled", px: 1.25, py: 1 }}>
              {safeItems.length === 0 ? emptyStateLabel : "No matches"}
            </Typography>
          ) : (
            filtered.map((item) => {
              const id = getId(item);
              const name = getName(item) || String(id || "").slice(0, 8);
              const detail = getDetail?.(item) || "";
              const selected = safeSelectedIds.includes(id);
              return (
                <Box
                  key={id}
                  onClick={() => handleToggle(id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    px: 1.25,
                    py: 0.6,
                    cursor: disabled ? "default" : "pointer",
                    borderRadius: "6px",
                    mx: 0.5,
                    backgroundColor: selected ? "action.selected" : "transparent",
                    "&:hover": {
                      backgroundColor: disabled ? "transparent" : "action.hover",
                    },
                  }}
                >
                  <AgentAvatar name={getAvatarLabel?.(item) || name} size={24} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "text.primary",
                        lineHeight: 1.3,
                      }}
                    >
                      {name}
                    </Typography>
                    {detail ? (
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "text.disabled",
                          lineHeight: 1.3,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {detail}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: "4px",
                      border: selected ? "none" : "1px solid",
                      borderColor: selected ? "transparent" : "divider",
                      backgroundColor: selected ? "text.primary" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.15s",
                    }}
                  >
                    {selected ? (
                      <CheckIcon sx={{ fontSize: 12, color: "#fff" }} />
                    ) : null}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
}
